import { useState, useEffect, useRef } from 'react';
import SectionCard from './SectionCard';
import PlayerToggle from './PlayerToggle';
import { MemberIdentity } from '../memberDisplay';
import { RandomOrderToggle, PlayerReorderList } from './SeedOrderControls';
import AddParticipantPanel from '../AddParticipantPanel';
import { useToast } from '../Toast';

/**
 * Список участников турнира с тумблерами.
 * Используется в TournamentPage (DRAFT) и TournamentEditView (RUNNING).
 *
 * @param {Array}    members      — результат useEventMembers
 * @param {boolean}  loading
 * @param {Function} isActive(organizationMemberId) → boolean
 * @param {Function} onToggle(organizationMemberId)
 * @param {number}   activeCount  — количество активных (для счётчика и предупреждения)
 * @param {number}   [minPlayers] — если передан и activeCount < minPlayers — показываем предупреждение
 *
 * Посев (random / manual order) — опционально, для форматов Mexicano / Beat the Box:
 * @param {boolean}  [seedingSupported] — показывать тумблер «Случайный порядок на старте»
 * @param {boolean}  [randomOrder]      — текущее значение (true = случайный)
 * @param {Function} [onRandomOrderChange](nextBool)
 * @param {Array}    [orderItems]       — активные игроки в текущем (черновом) порядке;
 *                                        в ручном режиме рендерятся как сортируемый блок
 * @param {Function} [onReorder](orderedItems) — новый порядок активных игроков
 * @param {boolean}  [reorderDisabled]  — запрет на перетаскивание (например, матчи сыграны)
 * @param {string}   [reorderHint]      — пояснение над сортируемым блоком
 *
 * Добавление нового участника (которого ещё нет в событии) — тот же `AddParticipantPanel`,
 * что и на странице события: он всегда работает поверх РЕГИСТРАЦИЙ СОБЫТИЯ (турнир не
 * умеет регистрировать участников напрямую, только брать их из уже зарегистрированных
 * в событии — см. TournamentService.addPlayer на бэке). Поэтому здесь нужны:
 * @param {string}   [eventId]           — publicId события турнира (без него — старое
 *                                          поведение: кнопка просто показывает тост)
 * @param {number}   [organizationId]    — для поиска участников организации в панели
 * @param {Function} [onParticipantAdded] — вызывается после успешного добавления в СОБЫТИЕ;
 *                                          родитель должен перезагрузить members (например,
 *                                          через useEventMembers().refetch), чтобы новый
 *                                          человек появился в списке (по умолчанию неактивен —
 *                                          включить его в турнир нужно тумблером и сохранить)
 *
 * UI режима добавления — по образцу «Изменить список» на странице события: кнопка в шапке
 * блока (не раскрывающаяся пунктирная кнопка под списком) переключает режим добавления;
 * `AddParticipantPanel` показывается НАД списком, пока режим включён, и не закрывается сама
 * после каждого добавления (можно добавить нескольких подряд) — закрывает только повторный
 * клик по кнопке. Тумблеры активности на строках участников не зависят от режима — это
 * турнирная особенность взамен посещаемости/оплаты события, она одинакова в обоих режимах.
 * Если после загрузки список участников события пуст — режим добавления включается сам
 * (актуально при создании турнира с чистого листа), и панель открывается сразу на вкладке
 * «Списком», а не «По одному».
 *
 * В ручном режиме (seedingSupported && !randomOrder) состав показывается единым списком:
 * сверху — активные игроки, которые можно перетаскивать/двигать стрелками (плашки физически
 * переезжают), снизу — неактивные/новые/удалённые без перетаскивания. Тумблер активности
 * остаётся на каждой строке.
 */
const TournamentMemberList = ({
  members, loading, isActive, onToggle, activeCount, minPlayers,
  seedingSupported = false,
  randomOrder = true,
  onRandomOrderChange,
  orderItems = [],
  onReorder,
  reorderDisabled = false,
  reorderHint,
  eventId,
  organizationId,
  onParticipantAdded,
}) => {
  const { addToast } = useToast();
  const [addMode, setAddMode] = useState(false);
  // Вкладка, с которой открывается AddParticipantPanel при следующем включении режима —
  // «Списком» при авто-открытии на пустом составе, «По одному» при ручном клике по кнопке.
  const [addPanelMode, setAddPanelMode] = useState('single');
  // Чтобы авто-открытие сработало один раз при первой загрузке пустого состава,
  // а не каждый раз, когда пользователь сам всех выключил/удалил.
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    if (!loading && members.length === 0 && eventId && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setAddPanelMode('list');
      setAddMode(true);
    }
  }, [loading, members.length, eventId]);

  const handleToggleAddMode = () => {
    if (!eventId) {
      // Фолбэк на случай, если родитель не передал eventId — старое поведение.
      addToast('Добавить участника можно на странице события — в разделе «Участники»', 'info');
      return;
    }
    setAddMode(v => {
      const next = !v;
      if (next) setAddPanelMode('single'); // ручное открытие — как на странице события
      return next;
    });
  };

  const showWarning = !loading && members.length > 0
    && minPlayers !== undefined
    && activeCount < minPlayers;

  // Ручной режим поддерживаемого формата → единый сортируемый список.
  const manualMode = seedingSupported && !randomOrder && !loading && members.length > 0;

  // Неактивные участники (для нижнего блока в ручном режиме): новые из события,
  // удалённые из турнира, удалённые из события — всё, что сейчас не ACTIVE.
  const inactiveMembers = manualMode
    ? members.filter(m => !isActive(m.organizationMemberId))
    : [];

  // Подписи-аннотации под именем (удалён из события / новый / лист ожидания).
  const Annotations = ({ member }) => (
    <>
      {member.removedFromEvent && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>удалён из события</div>
      )}
      {member.newToTournament && !member.waitingList && (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>новый в событии</div>
      )}
      {member.waitingList && (
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>в листе ожидания</div>
      )}
    </>
  );

  // Обычная строка участника (плоский список и нижний блок неактивных).
  const MemberRow = ({ member }) => {
    const active = isActive(member.organizationMemberId);
    const ghost  = !!member.removedFromEvent;
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', borderRadius: 10,
          border: ghost
            ? '1.5px dashed #d1d5db'
            : active ? '1.5px solid #f0f0f5' : '1.5px solid #fde8d8',
          background: ghost ? '#f9fafb' : active ? '#fff' : '#fff9f5',
          transition: 'background 0.15s',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, opacity: active ? 1 : 0.6 }}>
          <MemberIdentity member={member} size={32} />
          <Annotations member={member} />
        </div>
        <PlayerToggle
          active={active}
          onChange={() => onToggle(member.organizationMemberId)}
        />
      </div>
    );
  };

  return (
    <SectionCard
      title="Участники"
      action={
        !loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {members.length > 0 && seedingSupported && (
              <RandomOrderToggle
                value={randomOrder}
                onChange={onRandomOrderChange}
                disabled={reorderDisabled}
              />
            )}
            {members.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'none', letterSpacing: 0 }}>
                {activeCount} из {members.length}
              </span>
            )}
            {/* Переключатель режима добавления — по образцу «Изменить список» на странице
                события: кнопка в шапке, а не раскрывающаяся под списком. */}
            <button
              type="button"
              onClick={handleToggleAddMode}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', borderRadius: 8,
                border: `1.5px solid ${addMode ? '#86efac' : '#e0e7ff'}`,
                background: addMode ? '#dcfce7' : '#f5f3ff',
                color: addMode ? '#16a34a' : '#6366f1',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {addMode ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15"/>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              )}
              {addMode ? 'Готово' : 'Добавить участника'}
            </button>
          </div>
        )
      }
    >
      {/* Панель поиска/добавления — та же, что на странице события в режиме «Изменить
          список». Показывается НАД списком и не закрывается сама после добавления —
          можно добавить нескольких подряд, закрывает только повторный клик по кнопке.
          Добавляет человека в РЕГИСТРАЦИИ СОБЫТИЯ; чтобы он попал в турнир, после
          onParticipantAdded (список обновится) его нужно включить тумблером и
          сохранить состав. */}
      {!loading && addMode && eventId && (
        <AddParticipantPanel
          eventId={eventId}
          organizationId={organizationId}
          onAdded={onParticipantAdded}
          defaultMode={addPanelMode}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '8px 0' }}>
          Загрузка...
        </div>
      ) : members.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>
          Нет подтверждённых участников события
        </div>
      ) : manualMode ? (
        // ── Ручной режим: единый список (сортируемые активные + неактивные ниже) ──
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2, lineHeight: 1.4 }}>
            {reorderHint || 'Перетащите за иконку слева или стрелками ↑↓ — порядок задаёт посев на старте.'}
          </div>

          {orderItems.length === 0 ? (
            <div style={{ fontSize: 12, color: '#9ca3af', padding: '4px 0' }}>
              Нет активных игроков. Включите участников ниже, чтобы задать порядок.
            </div>
          ) : (
            <PlayerReorderList
              items={orderItems}
              getKey={(it) => it.organizationMemberId ?? it.id}
              onReorder={onReorder}
              disabled={reorderDisabled}
              renderAnnotation={(item) => <Annotations member={item} />}
              renderTrailing={(item) => (
                <PlayerToggle
                  active
                  onChange={() => onToggle(item.organizationMemberId)}
                />
              )}
            />
          )}

          {inactiveMembers.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: 0.4, margin: '6px 2px 8px',
              }}>
                Не участвуют
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {inactiveMembers.map(member => (
                  <MemberRow key={member.organizationMemberId} member={member} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        // ── Случайный режим / форматы без посева: плоский список ──
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {members.map(member => (
            <MemberRow key={member.organizationMemberId} member={member} />
          ))}
        </div>
      )}

      {showWarning && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', borderRadius: 9,
          background: '#fffbeb', border: '1px solid #fde68a',
          fontSize: 12, color: '#92400e', marginTop: 8,
        }}>
          <span>⚠️</span>
          <span>Для старта нужно минимум {minPlayers} активных игрока (сейчас {activeCount})</span>
        </div>
      )}
    </SectionCard>
  );
};

export default TournamentMemberList;
